import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

function normalizeIds(ids: string[]): string[] {
  return Array.from(new Set(ids.filter(Boolean))).sort()
}

const FAVORITES_QUERY_CHUNK_SIZE = 80

export async function fetchResourceFavoriteIds(userId: string, resourceIds: string[]): Promise<string[]> {
  const ids = normalizeIds(resourceIds)
  if (!userId || ids.length === 0) return []

  const favoriteSet = new Set<string>()

  for (let offset = 0; offset < ids.length; offset += FAVORITES_QUERY_CHUNK_SIZE) {
    const chunk = ids.slice(offset, offset + FAVORITES_QUERY_CHUNK_SIZE)

    const { data, error } = await supabase
      .from('resource_favorites')
      .select('resource_id')
      .eq('user_id', userId)
      .in('resource_id', chunk)

    if (error) throw error

    const rows = (data || []) as Array<{ resource_id: string }>
    for (const row of rows) {
      if (row.resource_id) {
        favoriteSet.add(row.resource_id)
      }
    }
  }

  return Array.from(favoriteSet)
}

export function useResourceFavorites(userId: string | null | undefined, resourceIds: string[]) {
  const ids = normalizeIds(resourceIds)

  return useQuery({
    queryKey: ['resourceFavorites', userId, ids],
    queryFn: async () => {
      if (!userId || ids.length === 0) return [] as string[]
      return fetchResourceFavoriteIds(userId, ids)
    },
    enabled: !!userId && ids.length > 0,
    staleTime: 30 * 1000,
  })
}

export function useToggleResourceFavorite(userId: string | null | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ resourceId, next }: { resourceId: string; next: boolean }) => {
      if (!userId) throw new Error('Not authenticated')
      const id = resourceId.trim()
      if (!id) throw new Error('Invalid resource id')

      if (next) {
        const { error } = await supabase
          .from('resource_favorites')
          .insert({ resource_id: id, user_id: userId } as Record<string, unknown>)
        if (error) throw error
        return
      }

      const { error } = await supabase
        .from('resource_favorites')
        .delete()
        .eq('resource_id', id)
        .eq('user_id', userId)
      if (error) throw error
    },
    onMutate: async ({ resourceId, next }) => {
      if (!userId) return { previousFavorites: [], previousResources: [] }

      await queryClient.cancelQueries({ queryKey: ['resourceFavorites', userId] })
      await queryClient.cancelQueries({ queryKey: ['accessibleResources'] })

      const previousFavorites = queryClient.getQueriesData<string[]>({ queryKey: ['resourceFavorites', userId] })
      const previousResources = queryClient.getQueriesData<any[]>({ queryKey: ['accessibleResources'] })

      for (const [key, data] of previousFavorites) {
        const current = Array.isArray(data) ? data : []
        const set = new Set(current)
        if (next) set.add(resourceId)
        else set.delete(resourceId)
        queryClient.setQueryData(key, Array.from(set))
      }

      for (const [key, data] of previousResources) {
        const current = Array.isArray(data) ? data : null
        if (!current) continue

        queryClient.setQueryData(
          key,
          current.map((r: any) => {
            if (!r || r.id !== resourceId) return r
            const prev = typeof r.favorite_count === 'number' ? r.favorite_count : 0
            const nextCount = next ? prev + 1 : Math.max(0, prev - 1)
            return { ...r, favorite_count: nextCount }
          })
        )
      }

      return { previousFavorites, previousResources }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return

      for (const [key, data] of ctx.previousFavorites) {
        queryClient.setQueryData(key, data)
      }
      for (const [key, data] of ctx.previousResources) {
        queryClient.setQueryData(key, data)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceFavorites'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
    },
  })
}
